/**
 * Company: SPHMedia
 * Description: [Gets Kaki makan video data]
 */

import fetchBHApi from "@helper/fetchBHApi";
import type { LatestVideoResponse } from "@typings/Brightcove.d";
import { PlaylistType } from "@typings/Playlist.d";
import { useCallback, useEffect, useMemo, useState } from "react";

type useKakiMakanVideoData = {
  playing: LatestVideoResponse | undefined;
  videos: LatestVideoResponse[];
  handleOnClickThumbnail: (props: LatestVideoResponse) => void;
  handleOnEndedVideo: () => void;
};

/**
 * Custom React Hook: useKakiMakanVideoHook
 * Description: [Gets Kaki makan videos from kaki makan bc playlist]
 * @returns data - The data returned by the hook
 */
export default function useKakiMakanVideo({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
}): useKakiMakanVideoData {
  const [playlistMode, setPlaylistMode] = useState(false);
  const [playlistVideos, setPlaylistVideos] = useState<LatestVideoResponse[]>();
  const [videos, setVideos] = useState<LatestVideoResponse[]>();
  const [playing, setPlaying] = useState<LatestVideoResponse | undefined>();

  useEffect(() => {
    const fetchData = async () => {
      let nowPlaying = false;
      try {
        const videos: LatestVideoResponse[] = await fetchBHApi<
          LatestVideoResponse[]
        >(
          `latest-videos-playlist?playlistId=${PlaylistType.KAKI_MAKAN}`,
          "GET",
        );
        setPlaylistMode(true);
        setPlaylistVideos(videos);

        setPlaying(videos[0]);
        nowPlaying = true;
        const updatedVideos = videos.map((video) =>
          video.id === videos[0].id
            ? { ...video, nowPlaying: nowPlaying ? true : false }
            : video,
        );
        setVideos(updatedVideos);
      } catch (error) {
        console.error("Something went wrong", error);
      }
    };
    void fetchData();
  }, []);

  const handleNowPlaying = useMemo(
    () => (id: string) => {
      setVideos(
        (prevValue) =>
          prevValue &&
          prevValue.map((video) => ({
            ...video,
            nowPlaying: video.id === id,
          })),
      );
    },
    [setVideos],
  );

  const handleOnClickThumbnail = useCallback(
    (props: LatestVideoResponse) => {
      // eslint-disable-next-line react/prop-types
      const { id } = props;
      handleNowPlaying(id);
      setPlaying(props);
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setPlaylistMode(false);
      const el = document.getElementById(`video-slide-${id}`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    },

    [handleNowPlaying, setPlaying, containerRef],
  );

  const handleOnEndedVideo = useCallback(() => {
    if (!playlistMode) return;
    if (!playlistVideos) return;
    let index: number = playlistVideos.findIndex(
      (video) => video.id === playing?.id,
    );
    if (index === playlistVideos.length - 1) {
      handleNowPlaying(playlistVideos[0].id);
      setPlaying(playlistVideos[0]);
    } else {
      index++;
      handleNowPlaying(playlistVideos[index].id);
      setPlaying(playlistVideos[index]);
    }
  }, [handleNowPlaying, playlistMode, playlistVideos, playing]);

  return {
    playing,
    videos: videos || [],
    handleOnClickThumbnail,
    handleOnEndedVideo,
  };
}
